const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

class AuthService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/auth`;
  }

  async request(endpoint, options = {}) {
    const token = localStorage.getItem('pilotleague_token');

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message || errorData.error || `HTTP error ${response.status}`
      );
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return response.json();
  }

  // Authentication endpoints
  async login({ email, password, rememberMe }) {
    return this.request('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, rememberMe }),
    });
  }

  async register({ username, email, password, acceptTerms, productUpdates = false }) {
    return this.request('/register', {
      method: 'POST',
      body: JSON.stringify({
        username,
        email,
        password,
        // Product-news consent from an UNTICKED box: recorded as proof (source 'signup')
        productUpdates: productUpdates === true,
        consents: [
          { type: 'terms_of_service', version: 'v1.0-2025-12-01' },
          { type: 'privacy_policy', version: 'v1.0-2025-12-01' },
        ],
      }),
    });
  }

  async logout() {
    return this.request('/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request('/me', {
      method: 'GET',
    });
  }

  async updateProfile(profileData) {
    return this.request('/me', {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });
  }

  async forgotPassword(email) {
    return this.request('/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async verifyEmail(token) {
    return this.request('/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // Google OAuth
  getGoogleAuthUrl() {
    return `${this.baseUrl}/google`;
  }

  async handleGoogleCallback(code) {
    return this.request('/google/callback', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // RGPD endpoints
  async exportData() {
    const token = localStorage.getItem('pilotleague_token');
    const response = await fetch(`${API_BASE_URL}/me/export`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.blob();
  }

  async deleteAccount() {
    return this.request('/me/delete', {
      method: 'POST',
    });
  }

  async getConsents() {
    const token = localStorage.getItem('pilotleague_token');
    const response = await fetch(`${API_BASE_URL}/me/consents`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.json();
  }

  async updateConsents(consents) {
    const token = localStorage.getItem('pilotleague_token');
    const response = await fetch(`${API_BASE_URL}/me/consents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ consents }),
    });
    return response.json();
  }

  // Token refresh
  async refreshToken() {
    const refreshToken = localStorage.getItem('pilotleague_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.request('/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });

    localStorage.setItem('pilotleague_token', response.token);
    if (response.refreshToken) {
      localStorage.setItem('pilotleague_refresh_token', response.refreshToken);
    }

    return response;
  }
}

export const authService = new AuthService();
export default authService;
